import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cityAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const CityView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cityAPI.getById(id);
      setRecord(response.data);
    } catch (err) {
      setErrorMessage(t('city.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadCity();
  }, [loadCity]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/city')}>
        <i className="fas fa-arrow-right"></i>
        {t('city.view.backToList')}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/city/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t('city.view.edit')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t('city.view.title')}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!record) {
    return (
      <AdminPage title={t('city.view.title')}>
        <StateBlock icon="fa-circle-exclamation" title={t('city.list.messages.noData')} />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const items = [
    { label: t('city.view.fields.cityCode'), value: record.cityCode },
    { label: t('city.view.fields.nameAr'), value: record.cityNameAr },
    { label: t('city.view.fields.nameEng'), value: record.cityNameEng },
    { label: t('city.view.fields.area'), value: record.area?.areaNameEng },
  ];

  return (
    <AdminPage
      title={record.cityNameAr || t('city.view.title')}
      subtitle={record.cityNameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-detail-grid">
          {items.map((item) => (
            <div key={item.label} className="ad-detail-item">
              <span className="ad-detail-label">{item.label}</span>
              <span className="ad-detail-value">
                {item.value === null || item.value === undefined || item.value === ''
                  ? '-'
                  : item.value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">{t('city.view.fields.isActive')}</span>
            <span>
              <span
                className={`ad-badge ${record.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {record.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default CityView;
